import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart, createSeriesMarkers, ColorType, CrosshairMode, LineStyle,
  LineSeries, AreaSeries, BaselineSeries, BarSeries, CandlestickSeries, HistogramSeries,
} from 'lightweight-charts';
import { api } from '../api/client';
import AddToWatchlist from '../components/AddToWatchlist';
import { getChartStyle, getTradeMarkerColors } from '../settings';
import { groupTrades, markerSize } from '../chartMarkers';
import { BellIcon, ClockIcon, WatchIcon } from '../components/Icons';

const COLORS = {
  border: '#2a2d3a',
  muted: '#64748b',
  text: '#f1f5f9',
  accent: '#3b82f6',
  gain: '#22c55e',
  loss: '#ef4444',
};

const hasOHLC = r => r.open != null && r.high != null && r.low != null;
const toOHLC = r => ({ time: r.time, open: r.open, high: r.high, low: r.low, close: r.value });
const toLine = r => ({ time: r.time, value: r.value });

function computeSMA(rows, period) {
  const out = [];
  let sum = 0;
  for (let i = 0; i < rows.length; i++) {
    sum += rows[i].value;
    if (i >= period) sum -= rows[i - period].value;
    if (i >= period - 1) out.push({ time: rows[i].time, value: sum / period });
  }
  return out;
}

function computeEMA(rows, period) {
  const out = [];
  const k = 2 / (period + 1);
  let ema = null;
  let sum = 0;
  for (let i = 0; i < rows.length; i++) {
    if (ema == null) {
      sum += rows[i].value;
      if (i === period - 1) {
        ema = sum / period;
        out.push({ time: rows[i].time, value: ema });
      }
    } else {
      ema = rows[i].value * k + ema * (1 - k);
      out.push({ time: rows[i].time, value: ema });
    }
  }
  return out;
}

function computeMA(rows, cfg) {
  return cfg.ma_type === 'EMA' ? computeEMA(rows, cfg.period) : computeSMA(rows, cfg.period);
}

function isMarketClosedToday() {
  const day = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short' });
  return day === 'Sat' || day === 'Sun';
}

// One entry per series type TradingView Lightweight Charts offers via chart.addSeries(...).
const SERIES_DEFS = {
  line: {
    ctor: LineSeries,
    options: { color: COLORS.accent, lineWidth: 2, priceFormat: { type: 'price', precision: 2, minMove: 0.01 }, crosshairMarkerVisible: true },
    mapData: rows => rows.map(toLine),
  },
  area: {
    ctor: AreaSeries,
    options: {
      lineColor: COLORS.accent, lineWidth: 2,
      topColor: 'rgba(59,130,246,0.4)', bottomColor: 'rgba(59,130,246,0.05)',
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 }, crosshairMarkerVisible: true,
    },
    mapData: rows => rows.map(toLine),
  },
  baseline: {
    ctor: BaselineSeries,
    options: {
      topLineColor: COLORS.gain, topFillColor1: 'rgba(34,197,94,0.3)', topFillColor2: 'rgba(34,197,94,0.05)',
      bottomLineColor: COLORS.loss, bottomFillColor1: 'rgba(239,68,68,0.05)', bottomFillColor2: 'rgba(239,68,68,0.3)',
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 }, crosshairMarkerVisible: true,
    },
    mapData: rows => rows.map(toLine),
    extraOptions: rows => rows.length ? { baseValue: { type: 'price', price: rows.reduce((s, r) => s + r.value, 0) / rows.length } } : {},
  },
  bar: {
    ctor: BarSeries,
    options: { upColor: COLORS.gain, downColor: COLORS.loss, thinBars: false },
    mapData: rows => rows.filter(hasOHLC).map(toOHLC),
  },
  candlestick: {
    ctor: CandlestickSeries,
    options: { upColor: COLORS.gain, downColor: COLORS.loss, borderVisible: false, wickUpColor: COLORS.gain, wickDownColor: COLORS.loss },
    mapData: rows => rows.filter(hasOHLC).map(toOHLC),
  },
  histogram: {
    ctor: HistogramSeries,
    options: { color: COLORS.accent, priceFormat: { type: 'price', precision: 2, minMove: 0.01 } },
    mapData: rows => rows.map(toLine),
  },
};

// Money market symbols chart their 7-day yield (percent) rather than a price.
// A yield has no open/high/low, so it is always a line whatever the chosen style.
const YIELD_DEF = {
  ctor: LineSeries,
  options: { color: COLORS.accent, lineWidth: 2, priceFormat: { type: 'percent', precision: 2, minMove: 0.01 }, crosshairMarkerVisible: true },
  mapData: rows => rows.map(toLine),
};

const DEFAULT_SYMBOL = 'SPY';

// Cost per share of the lots still held, across every account holding the
// symbol. total_cost is the cost of those specific lots (as on the Positions page).
function blendedAvgCost(positions, symbol) {
  const held = positions.filter(p => p.symbol === symbol && p.quantity > 0);
  const cost = held.reduce((sum, p) => sum + p.total_cost, 0);
  const quantity = held.reduce((sum, p) => sum + p.quantity, 0);
  return quantity > 0 ? cost / quantity : null;
}

// Open lots merged by cost per share (to the cent) across accounts, so
// repeat buys at the same price draw a single line.
function mergeLots(lots) {
  const byPrice = new Map();
  lots.forEach(l => {
    const price = Math.round(l.cost_per_share * 100) / 100;
    byPrice.set(price, (byPrice.get(price) || 0) + l.quantity);
  });
  return [...byPrice].map(([price, quantity]) => ({ price, quantity }));
}

const fmtPct = pct => `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
const fmtQty = q => Number(q.toFixed(4)).toLocaleString();

export default function Charts({ navParams = {}, onNavigate }) {
  const [symbol, setSymbol]     = useState(() => navParams.symbol || DEFAULT_SYMBOL);
  const [input, setInput]       = useState(() => navParams.symbol || DEFAULT_SYMBOL);
  const [daily, setDaily]       = useState([]);
  const [intraday, setIntraday] = useState([]);
  const [isYield, setIsYield]   = useState(false); // loaded symbol is a money market symbol
  const [tab, setTab]           = useState('daily');
  const [chartStyle]            = useState(getChartStyle);
  const [maConfig, setMaConfig] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [watchSymbol, setWatchSymbol] = useState(null);
  const [recent, setRecent]     = useState([]);
  const [recentSort, setRecentSort] = useState('recent'); // 'recent' | 'alpha' | 'alpha-desc'
  const [recentDir, setRecentDir] = useState('desc'); // 'desc' (most recent first) | 'asc' (least recent first) — applies when recentSort === 'recent'
  const [positions, setPositions] = useState([]);
  const [lots, setLots]         = useState([]);
  const [showLots, setShowLots] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showTrades, setShowTrades] = useState(true);
  const [markerColors]          = useState(getTradeMarkerColors);
  const [accounts, setAccounts] = useState([]);

  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const maSeriesRef = useRef([]);
  const tooltipRef = useRef(null);
  const legendRef = useRef(null);
  const costLabelsRef = useRef(null);
  const markerHighlightRef = useRef(null);
  const tradesByTimeRef = useRef(new Map());
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const isYieldRef = useRef(isYield);
  isYieldRef.current = isYield;
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    api.getMovingAverages().then(setMaConfig).catch(() => setMaConfig([]));
    api.getPositions().then(setPositions).catch(() => setPositions([]));
    api.getAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  // Cost lines are prices, so they have no place on a yield chart.
  const avgCost = useMemo(() => (isYield ? null : blendedAvgCost(positions, symbol)), [isYield, positions, symbol]);

  useEffect(() => {
    setLots([]);
    if (!symbol) return;
    let cancelled = false;
    api.getPositionLots(symbol)
      .then(rows => { if (!cancelled) setLots(mergeLots(rows || [])); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbol]);

  const visibleLots = useMemo(() => (showLots && !isYield ? lots : []), [showLots, isYield, lots]);

  useEffect(() => {
    setTransactions([]);
    if (!symbol) return;
    let cancelled = false;
    api.getTransactions({ symbol })
      .then(rows => { if (!cancelled) setTransactions(rows || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbol]);

  // A yield series is daily only, so the Intraday tab never applies to it.
  const view = isYield ? 'daily' : tab;

  // Transaction dates are day-granularity, so markers only go on the Daily tab.
  const tradeGroups = useMemo(
    () => (showTrades && view === 'daily' ? groupTrades(transactions, daily) : []),
    [showTrades, view, transactions, daily],
  );

  const loadRecent = useCallback(() => {
    api.getRecentlyViewed().then(setRecent).catch(() => {});
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  useEffect(() => {
    if (navParams.symbol) {
      setInput(navParams.symbol);
      setSymbol(navParams.symbol);
    }
  }, [navParams.symbol]);

  useEffect(() => {
    if (symbol) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const search = async () => {
    if (!input.trim()) return;
    const s = input.trim().toUpperCase();
    setSymbol(s);
    setLoading(true);
    setError('');
    try {
      // Money market symbols are never price-fetched; their chart is the yield history.
      const moneyMarket = await api.getMoneyMarketSymbols().catch(() => []);
      const yieldSymbol = moneyMarket.some(m => m.symbol === s);
      if (yieldSymbol) {
        const rates = await api.getMoneyMarketRates(s); // newest first
        setDaily(rates.map(r => ({ time: r.effective_date, value: r.rate_pct })).reverse());
        setIntraday([]);
      } else {
        const [d, id] = await Promise.all([api.getDailyPrices(s), api.getIntradayPrices(s)]);
        setDaily(d.map(r => ({ time: r.date, value: r.close_price, open: r.open_price, high: r.high_price, low: r.low_price, volume: r.volume })));
        setIntraday(id.map(r => ({ time: Math.floor(new Date(r.timestamp).getTime() / 1000), value: r.close_price, open: r.open_price, high: r.high_price, low: r.low_price })));
      }
      setIsYield(yieldSymbol);
      api.recordSymbolView(s).then(loadRecent).catch(() => {});
    } catch {
      setError(`Could not load chart data for ${s}`);
    } finally {
      setLoading(false);
    }
  };

  const selectRecent = sym => {
    setInput(sym);
    setSymbol(sym);
  };

  const toggleRecentSort = () => {
    if (recentSort === 'recent') {
      setRecentDir(recentDir === 'desc' ? 'asc' : 'desc');
    } else {
      setRecentSort('recent');
      setRecentDir('desc');
    }
  };

  const toggleAlphaSort = () => {
    setRecentSort(recentSort === 'alpha' ? 'alpha-desc' : 'alpha');
  };

  const sortedRecent = [...recent].sort((a, b) => {
    if (recentSort === 'alpha') return a.symbol.localeCompare(b.symbol);
    if (recentSort === 'alpha-desc') return b.symbol.localeCompare(a.symbol);
    const diff = new Date(b.last_viewed_at) - new Date(a.last_viewed_at);
    return recentDir === 'asc' ? -diff : diff;
  });

  const data = view === 'daily' ? daily : intraday;

  const containerRef = useCallback(node => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeSeriesRef.current = null;
      maSeriesRef.current = [];
    }
    if (!node) return;

    const chart = createChart(node, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: COLORS.muted,
        fontSize: 11,
      },
      grid: {
        vertLines: { color: COLORS.border },
        horzLines: { color: COLORS.border },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: COLORS.border, scaleMargins: { top: 0.1, bottom: 0.3 } },
      timeScale: { borderColor: COLORS.border },
      autoSize: true,
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale('volume').applyOptions({
      visible: false,
      scaleMargins: { top: 0.8, bottom: 0 },
    });
    chart.subscribeCrosshairMove(param => {
      const tooltipEl = tooltipRef.current;
      const series = seriesRef.current;
      if (!tooltipEl || !series) return;
      const point = param.seriesData.get(series);
      if (!param.point || !point) {
        tooltipEl.style.display = 'none';
        return;
      }
      const value = point.value != null ? point.value : point.close;
      const volPoint = param.seriesData.get(volumeSeries);
      const label = typeof param.time === 'number'
        ? new Date(param.time * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : String(param.time);
      const trades = tradesByTimeRef.current.get(param.time) || [];
      tooltipEl.style.display = 'block';
      tooltipEl.innerHTML =
        `<div style="font-size:11px;color:${COLORS.muted};margin-bottom:4px">${label}</div>` +
        `<div style="font-family:monospace;color:${COLORS.accent};font-size:15px">${isYieldRef.current ? `${value.toFixed(2)}%` : `$${value.toFixed(2)}`}</div>` +
        (volPoint?.value != null
          ? `<div style="font-size:11px;color:${COLORS.muted};margin-top:2px">Vol ${Math.round(volPoint.value).toLocaleString()}</div>`
          : '') +
        trades.map(t =>
          `<div style="font-size:11px;color:${COLORS.text};margin-top:4px;white-space:nowrap">` +
          `<span style="color:${t.color}">${t.type === 'buy' ? '▲' : '▼'}</span> ` +
          `${t.type === 'buy' ? 'Bought' : 'Sold'} ${fmtQty(t.quantity)} sh @ $${t.price.toFixed(2)}` +
          (t.account ? ` <span style="color:${COLORS.muted}">· ${t.account}</span>` : '') +
          `</div>`).join('');
      // Right of the cursor, flipping to its left over the rightmost quarter of the chart.
      const left = param.point.x > node.clientWidth * 0.75
        ? param.point.x - 12 - tooltipEl.offsetWidth
        : param.point.x + 12;
      tooltipEl.style.left = Math.max(0, Math.min(left, node.clientWidth - tooltipEl.offsetWidth - 4)) + 'px';
      // Sits on the far side of the cursor from the line, so it never covers it:
      // above the cursor when the crosshair is above the line, below otherwise.
      const lineY = series.priceToCoordinate(value);
      const above = lineY != null && param.point.y < lineY;
      tooltipEl.style.top = (above ? Math.max(0, param.point.y - 12 - tooltipEl.offsetHeight) : param.point.y + 12) + 'px';
    });

    chartRef.current = chart;
    volumeSeriesRef.current = volumeSeries;

    resizeObserverRef.current = new ResizeObserver(() => chart.timeScale().fitContent());
    resizeObserverRef.current.observe(node);
  }, []);

  // (Re)creates the main price series whenever the chosen style, data, or tab
  // changes — lightweight-charts series can't switch type in place.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current);
      seriesRef.current = null;
    }

    const def = isYield ? YIELD_DEF : SERIES_DEFS[chartStyle] || SERIES_DEFS.line;
    const seriesData = def.mapData(data);
    const extraOptions = def.extraOptions ? def.extraOptions(seriesData) : {};
    const last = data.length ? data[data.length - 1].value : null;
    const pctFrom = cost => (last != null ? ((last - cost) / cost) * 100 : null);
    const colorFor = pct => (pct != null && pct < 0 ? COLORS.loss : COLORS.gain);
    const costLines = [];
    if (avgCost != null) {
      const pct = pctFrom(avgCost);
      costLines.push({
        price: avgCost,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        text: pct != null ? `Avg cost ${fmtPct(pct)}` : 'Avg cost',
        color: colorFor(pct),
        bold: true,
      });
    }
    visibleLots.forEach(lot => {
      const pct = pctFrom(lot.price);
      costLines.push({
        price: lot.price,
        lineStyle: LineStyle.Dotted,
        axisLabelVisible: false,
        text: `${fmtQty(lot.quantity)} sh @ $${lot.price.toFixed(2)}${pct != null ? ' ' + fmtPct(pct) : ''}`,
        color: colorFor(pct),
        below: true, // keeps lot labels clear of the avg cost label, which sits above its line
      });
    });

    // Price lines don't count toward autoscale, so widen the range to keep the
    // cost lines on screen even when they're far from the plotted prices.
    const costPrices = costLines.map(l => l.price);
    const autoscaleInfoProvider = costPrices.length === 0 ? undefined : original => {
      const res = original();
      if (!res) return res;
      return {
        ...res,
        priceRange: {
          minValue: Math.min(res.priceRange.minValue, ...costPrices),
          maxValue: Math.max(res.priceRange.maxValue, ...costPrices),
        },
      };
    };
    const series = chart.addSeries(def.ctor, { ...def.options, ...extraOptions, autoscaleInfoProvider });
    series.setData(seriesData);
    seriesRef.current = series;

    // Trade markers point at the trade price: buys from below, sells from above.
    // On a yield chart the trade price ($1.00) is off the scale, so they mark the
    // date only, below and above the line.
    createSeriesMarkers(series, tradeGroups.map(g => ({
      id: g.id,
      time: g.time,
      ...(isYield
        ? { position: g.type === 'buy' ? 'belowBar' : 'aboveBar' }
        : { price: g.price, position: g.type === 'buy' ? 'atPriceBottom' : 'atPriceTop' }),
      shape: g.type === 'buy' ? 'arrowUp' : 'arrowDown',
      color: markerColors[g.type],
    })));
    const groupsById = new Map(tradeGroups.map(g => [g.id, g]));
    const accountNames = new Map(accounts.map(a => [a.id, a.name]));
    const multiAccount = new Set(transactions.map(t => t.account_id)).size > 1;
    const tradesByTime = new Map();
    tradeGroups.forEach(g => {
      const lines = tradesByTime.get(g.time) || [];
      g.trades.forEach(t => lines.push({
        type: g.type,
        quantity: t.quantity,
        price: t.price_per_share,
        account: multiAccount ? accountNames.get(t.account_id) : null,
        color: markerColors[g.type],
      }));
      tradesByTime.set(g.time, lines);
    });
    tradesByTimeRef.current = tradesByTime;

    // Hovering a marker draws a highlight box behind it (the chart canvas is
    // transparent) and makes it clickable through to the symbol's transactions.
    // Yield chart markers aren't at a price, so they get the click but no box.
    let hoveredGroup = null;
    const highlightEl = markerHighlightRef.current;
    const chartEl = chart.chartElement();
    const placeHighlight = () => {
      if (!highlightEl) return;
      const x = hoveredGroup && chart.timeScale().timeToCoordinate(hoveredGroup.time);
      const priceY = hoveredGroup && !isYield ? series.priceToCoordinate(hoveredGroup.price) : null;
      if (x == null || priceY == null) {
        highlightEl.style.display = 'none';
        return;
      }
      const size = markerSize(chart.timeScale().options().barSpacing);
      const box = size + 10;
      const centerY = hoveredGroup.type === 'buy' ? priceY + size / 2 : priceY - size / 2;
      Object.assign(highlightEl.style, {
        display: 'block',
        left: x - box / 2 + 'px',
        top: centerY - box / 2 + 'px',
        width: box + 'px',
        height: box + 'px',
      });
    };
    const markerHoverHandler = param => {
      const group = groupsById.get(param.hoveredObjectId) || null;
      if (group === hoveredGroup) return;
      hoveredGroup = group;
      chartEl.style.cursor = group ? 'pointer' : '';
      placeHighlight();
    };
    const markerClickHandler = param => {
      const group = groupsById.get(param.hoveredObjectId);
      if (group && onNavigateRef.current) onNavigateRef.current('transactions', { symbol: group.trades[0].symbol });
    };
    chart.subscribeCrosshairMove(markerHoverHandler);
    chart.subscribeClick(markerClickHandler);
    chart.timeScale().subscribeVisibleLogicalRangeChange(placeHighlight);
    placeHighlight();

    // OHLC legend tracks the hovered bar, falling back to the latest bar. Line
    // styles only carry `value` in seriesData, so look the full row up by time.
    const legendEl = legendRef.current;
    const rowsByTime = new Map(data.map(r => [r.time, r]));
    const renderLegend = row => {
      if (!legendEl) return;
      if (!row || isYield) {
        legendEl.innerHTML = '';
        return;
      }
      // Rows without full OHLC show close only, uncolored, with dashes elsewhere.
      const full = hasOHLC(row);
      const color = !full ? COLORS.text : row.value < row.open ? COLORS.loss : COLORS.gain;
      const fields = [['O', row.open], ['H', row.high], ['L', row.low]].map(([k, v]) => [k, full ? v : null]);
      legendEl.innerHTML = [...fields, ['C', row.value]]
        .map(([k, v]) =>
          `<span style="color:${COLORS.muted}">${k}</span> ` +
          (v != null ? `<span style="color:${color}">${v.toFixed(2)}</span>` : `<span style="color:${COLORS.muted}">—</span>`))
        .join('&nbsp;&nbsp;');
    };
    const legendHandler = param => {
      const row = param.time != null ? rowsByTime.get(param.time) : null;
      renderLegend(row || data[data.length - 1]);
    };
    chart.subscribeCrosshairMove(legendHandler);
    renderLegend(data[data.length - 1]);

    // Price line titles can only render beside the right axis, so cost labels
    // are HTML overlays pinned to the left edge that track each line's y.
    let rafId = null;
    let hoverHandler = null;
    const labelsEl = costLabelsRef.current;
    if (labelsEl) labelsEl.innerHTML = '';
    if (labelsEl && costLines.length) {
      const tracked = costLines.map(line => {
        series.createPriceLine({
          price: line.price,
          color: line.color,
          lineWidth: 1,
          lineStyle: line.lineStyle,
          axisLabelVisible: line.axisLabelVisible,
          title: '',
        });
        const el = document.createElement('div');
        el.textContent = line.text;
        Object.assign(el.style, {
          display: 'none',
          position: 'absolute',
          left: '6px',
          transform: line.below ? 'none' : 'translateY(-100%)',
          [line.below ? 'marginTop' : 'marginBottom']: '2px',
          padding: '1px 5px',
          border: '1px solid transparent',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: line.bold ? '600' : '400',
          color: line.color,
          whiteSpace: 'nowrap',
        });
        labelsEl.appendChild(el);
        return { el, price: line.price, lastY: undefined };
      });
      const track = () => {
        tracked.forEach(t => {
          const y = series.priceToCoordinate(t.price);
          if (y !== t.lastY) {
            t.lastY = y;
            t.el.style.display = y == null ? 'none' : 'block';
            if (y != null) t.el.style.top = y + 'px';
          }
        });
        rafId = requestAnimationFrame(track);
      };
      track();

      // Labels ignore pointer events so the crosshair keeps working over them;
      // hit-test the crosshair point instead to highlight the hovered label.
      hoverHandler = param => {
        const origin = labelsEl.getBoundingClientRect();
        tracked.forEach(t => {
          const r = t.el.getBoundingClientRect();
          const hit = param.point != null && t.el.style.display !== 'none'
            && param.point.x >= r.left - origin.left && param.point.x <= r.right - origin.left
            && param.point.y >= r.top - origin.top && param.point.y <= r.bottom - origin.top;
          t.el.style.background = hit ? 'var(--bg)' : 'transparent';
          t.el.style.borderColor = hit ? 'var(--border)' : 'transparent';
          t.el.style.zIndex = hit ? '1' : '';
        });
      };
      chart.subscribeCrosshairMove(hoverHandler);
    }

    if (view === 'daily') {
      let prev = null;
      volumeSeriesRef.current.setData(data.filter(r => r.volume != null).map(r => {
        const color = prev != null && r.value < prev ? COLORS.loss : COLORS.gain;
        prev = r.value;
        return { time: r.time, value: r.volume, color };
      }));
    } else {
      volumeSeriesRef.current.setData([]);
    }

    maSeriesRef.current.forEach(s => chart.removeSeries(s));
    maSeriesRef.current = (isYield ? [] : maConfig).map(cfg => {
      const maSeries = chart.addSeries(LineSeries, {
        color: cfg.color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      maSeries.setData(computeMA(data, cfg));
      return maSeries;
    });

    chart.timeScale().fitContent();
    if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      // The chart may already be removed on unmount (the ref callback runs first).
      if (chartRef.current === chart) {
        if (hoverHandler) chart.unsubscribeCrosshairMove(hoverHandler);
        chart.unsubscribeCrosshairMove(markerHoverHandler);
        chart.unsubscribeCrosshairMove(legendHandler);
        chart.unsubscribeClick(markerClickHandler);
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(placeHighlight);
        chartEl.style.cursor = '';
      }
      if (highlightEl) highlightEl.style.display = 'none';
    };
  }, [data, view, isYield, chartStyle, maConfig, avgCost, visibleLots, tradeGroups, markerColors, accounts, transactions]);

  const hasChart = !loading && !error && data.length > 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Charts</h1>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input
          style={{ width: 160 }}
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Symbol (e.g. AAPL)"
        />
        <button className="btn" onClick={search}>Load</button>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div className="card" style={{ width: 150, flexShrink: 0, padding: 12 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Recent</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                className={recentSort === 'recent' ? 'btn' : 'btn-ghost'}
                style={{ flex: 1, height: 28, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={toggleRecentSort}
                title={recentSort === 'recent' && recentDir === 'asc' ? 'Least Recent' : 'Recent'}
                aria-label="Sort by last viewed"
              ><ClockIcon style={{ transform: recentDir === 'asc' ? 'rotate(180deg)' : 'none' }} /></button>
              <button
                className={recentSort === 'alpha' || recentSort === 'alpha-desc' ? 'btn' : 'btn-ghost'}
                style={{ flex: 1, height: 28, padding: 0, fontSize: 11 }}
                onClick={toggleAlphaSort}
                title={recentSort === 'alpha-desc' ? 'Sort A to Z' : 'Sort Z to A'}
              >{recentSort === 'alpha-desc' ? 'Z-A' : 'A-Z'}</button>
            </div>
          </div>
          {sortedRecent.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>No symbols viewed yet.</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sortedRecent.map(r => (
                <button
                  key={r.symbol}
                  onClick={() => selectRecent(r.symbol)}
                  className={r.change_pct >= 0 ? 'gain' : r.change_pct < 0 ? 'loss' : ''}
                  style={{
                    background: r.symbol === symbol ? 'var(--surface)' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    padding: '6px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {r.symbol}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {symbol && !isYield && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <button className={tab === 'daily' ? 'btn' : 'btn-ghost'} onClick={() => setTab('daily')}>Daily</button>
              <button className={tab === 'intraday' ? 'btn' : 'btn-ghost'} onClick={() => setTab('intraday')}>Intraday</button>
            </div>
          )}

          <div className="card" style={{ display: symbol ? 'block' : 'none' }}>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{symbol}</span>
              <span className="muted">{isYield ? '7-Day Yield' : tab === 'daily' ? 'Daily Close' : 'Intraday 5-min'}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {!isYield && lots.length > 0 && (
                  <button
                    className={showLots ? 'btn' : 'btn-ghost'}
                    style={{ fontSize: 12, padding: '3px 10px' }}
                    title="Show a price line for each open lot"
                    onClick={() => setShowLots(v => !v)}
                  >
                    Lots ({lots.length})
                  </button>
                )}
                {transactions.length > 0 && (
                  <button
                    className={showTrades ? 'btn' : 'btn-ghost'}
                    style={{ fontSize: 12, padding: '3px 10px' }}
                    title={isYield ? 'Mark the dates of your buys and sells' : 'Mark your buys and sells on the Daily chart'}
                    onClick={() => setShowTrades(v => !v)}
                  >
                    Trades ({transactions.length})
                  </button>
                )}
                <button className="icon-btn-sm" title="Watch" aria-label="Watch" onClick={() => setWatchSymbol(symbol)}><WatchIcon /></button>
                {!isYield && <button className="icon-btn-sm" title="Alert" aria-label="Alert" onClick={() => onNavigate && onNavigate('alerts', { symbol })}><BellIcon /></button>}
              </div>
            </div>

            <div style={{ position: 'relative', height: 320 }}>
              {loading && <div className="muted" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}
              {error && <div className="loss" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{error}</div>}
              {!loading && !error && data.length === 0 && (
                <div className="muted" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 20px' }}>
                  {isYield ? (
                    <span>
                      No yield history for {symbol} yet. Import a Fidelity history file on the{' '}
                      <button
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--accent)', cursor: 'pointer' }}
                        onClick={() => onNavigate && onNavigate('data')}
                      >Data page</button>.
                    </span>
                  ) : tab === 'intraday' && isMarketClosedToday()
                    ? 'No intraday chart data. Markets are closed today.'
                    : `No ${tab} data yet for ${symbol}. Trigger a price refresh from the Dashboard.`}
                </div>
              )}
              <div
                ref={markerHighlightRef}
                style={{
                  display: 'none',
                  position: 'absolute',
                  zIndex: 0,
                  pointerEvents: 'none',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                }}
              />
              <div
                ref={containerRef}
                style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', visibility: hasChart ? 'visible' : 'hidden' }}
              />
              <div
                ref={costLabelsRef}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  visibility: hasChart ? 'visible' : 'hidden',
                }}
              />
              <div
                ref={legendRef}
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 8,
                  zIndex: 2,
                  pointerEvents: 'none',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  visibility: hasChart ? 'visible' : 'hidden',
                }}
              />
              <div
                ref={tooltipRef}
                style={{
                  display: 'none',
                  position: 'absolute',
                  pointerEvents: 'none',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  padding: '8px 12px',
                  borderRadius: 6,
                  zIndex: 3,
                }}
              />
            </div>
          </div>

          {!symbol && (
            <div className="card muted" style={{ textAlign: 'center', padding: 60 }}>
              Enter a symbol above to view price charts.
            </div>
          )}
        </div>
      </div>

      {watchSymbol && <AddToWatchlist symbol={watchSymbol} onClose={() => setWatchSymbol(null)} />}
    </div>
  );
}
